import type {
  PlatformEvent,
  RunReasoningDeltaData,
  RunResponseDeltaData,
  RunResponseStopData,
  RunToolCallData,
  RunToolResultData,
  RunFinalAnswerDeltaData,
} from '../types/events';
import type { SubAgentGroup } from './content-builder';
import { createIterationState, applyToolResult, findOrCreateSubAgent } from './content-builder';

// Re-export types for consumers
export type { SubAgentState, SubAgentGroup, SubAgentStatus } from './content-builder';

/**
 * Routes a standard agent.run.* event (with parentRunId) to the correct
 * SubAgentState within the groups, updating its iteration state.
 */
export function handleSubAgentProgressEvent(
  event: PlatformEvent,
  subAgentGroups: SubAgentGroup[],
): boolean {
  const data = event.data as Record<string, unknown>;
  const eventRunId = (data.runId ?? event.subjectId) as string | undefined;
  if (!eventRunId) return false;

  // Find the sub-agent across all groups, lazily creating it if missing.
  // Child progress events can race ahead of `subagent_started` (WS ordering is
  // not guaranteed); they carry agentId/taskId but not agentName, which the
  // lifecycle events backfill.
  const sa = findOrCreateSubAgent(subAgentGroups, eventRunId, {
    agentId: data.agentId as string | undefined,
    taskId: data.taskId as string | undefined,
  });

  function getOrCreateSaIteration(iter: number) {
    let state = sa.iterations.get(iter);
    if (!state) {
      state = createIterationState();
      sa.iterations.set(iter, state);
    }
    return state;
  }

  switch (event.eventType) {
    case 'agent.run.response_start': {
      const d = data as unknown as { iteration: number };
      sa.currentIteration = d.iteration;
      getOrCreateSaIteration(d.iteration);
      break;
    }
    case 'agent.run.reasoning_start': {
      const d = data as unknown as { iteration: number };
      sa.currentIteration = d.iteration;
      getOrCreateSaIteration(d.iteration);
      break;
    }
    case 'agent.run.reasoning_delta': {
      const d = data as unknown as RunReasoningDeltaData;
      const iter = getOrCreateSaIteration(d.iteration);
      if (d.accumulated != null) {
        if (d.accumulated.length >= iter.reasoningText.length) {
          iter.reasoningText = d.accumulated;
        }
      } else if (d.delta) {
        iter.reasoningText += d.delta;
      }
      // Aggregate to top-level — Map insertion order is sufficient (monotonic iteration numbers)
      sa.reasoningText = '';
      for (const [, it] of sa.iterations) {
        if (it.reasoningText) sa.reasoningText += it.reasoningText;
      }
      break;
    }
    case 'agent.run.response_delta': {
      const d = data as unknown as RunResponseDeltaData;
      const iter = getOrCreateSaIteration(d.iteration);
      if (d.accumulated != null) {
        if (d.accumulated.length >= iter.responseText.length) {
          iter.responseText = d.accumulated;
        }
      } else if (d.delta) {
        iter.responseText += d.delta;
      }
      // Aggregate to top-level — Map insertion order is sufficient
      sa.responseText = '';
      for (const [, it] of sa.iterations) {
        if (it.responseText) sa.responseText += it.responseText;
      }
      break;
    }
    case 'agent.run.response_stop': {
      const d = data as unknown as RunResponseStopData;
      // See run-stream-state.ts: do not flip isThinking on isFinal.
      // final_answer_start / completed are responsible for finalising it.
      if (d.content != null) {
        const iter = getOrCreateSaIteration(d.iteration ?? sa.currentIteration);
        if (d.content.length >= iter.responseText.length) {
          iter.responseText = d.content;
        }
      }
      break;
    }
    case 'agent.run.tool_call': {
      const d = data as unknown as RunToolCallData;
      const iter = getOrCreateSaIteration(d.iteration ?? sa.currentIteration);
      iter.isThinking = true;
      iter.toolCalls.set(d.toolCallId, {
        toolName: d.toolName,
        toolDisplayName: d.toolDisplayName,
        toolCallId: d.toolCallId,
        input: d.input,
        // Carry the backend-authored CoT label (e.g. "Searching …", "Fetching …")
        // so the sub-agent activity row shows it, mirroring the main-agent path.
        label: d.label,
      });
      sa.toolCallCount++;
      break;
    }
    case 'agent.run.tool_result': {
      const d = data as unknown as RunToolResultData;
      applyToolResult(sa.iterations, d);
      break;
    }
    case 'agent.run.final_answer_start': {
      const d = data as unknown as { iteration: number };
      sa.currentIteration = d.iteration;
      const iter = getOrCreateSaIteration(d.iteration);
      iter.hasFinalAnswer = true;
      for (const [, prevIter] of sa.iterations) {
        if (prevIter.isThinking === null || prevIter.isThinking === false) {
          prevIter.isThinking = true;
        }
      }
      break;
    }
    case 'agent.run.final_answer_delta': {
      const d = data as unknown as RunFinalAnswerDeltaData;
      const iter = getOrCreateSaIteration(d.iteration);
      if (d.accumulated != null) {
        if (d.accumulated.length >= iter.finalAnswerText.length) {
          iter.finalAnswerText = d.accumulated;
        }
      } else if (d.delta) {
        iter.finalAnswerText += d.delta;
      }
      sa.responseText = iter.finalAnswerText;
      break;
    }
    case 'agent.run.final_answer_stop': {
      break; // no-op; completed follows
    }
    default:
      return false;
  }
  return true;
}
